import { FrontendPaymentSource } from '../enums/FrontendPaymentSource';
import { PaymentMethodEnum } from '../enums/PaymentMethodEnum';
import { SubscriptionPlanCategoryEnum } from '../enums/PartnerSubscription';
import { isPaidUser, isPmaWebview } from '../helpers/partner-utils';
import { Partner } from '../interfaces/partner/PartnerProfile';

export type FunnelTouchpoint =
    | 'post_job'
    | 'applicant_contacts'
    | 'profile_view'
    | 'saud_interviews'
    | 'advanced_filters'
    | 'job_promotion'
    | 'billing_page';

export type FunnelUserState = 'free' | 'paid';

export type FunnelSurface = 'pwa' | 'pma';

export type FunnelPlan = 'free' | 'basic' | 'premium' | 'premium_plus' | 'enterprise';

const TOUCHPOINT_STORAGE_KEY = 'sabbarFunnelTouchpoint';

const DEFAULT_TOUCHPOINT: FunnelTouchpoint = 'post_job';

/**
 * Maps the redux payment source to the analytics touchpoint. Returns undefined for
 * sources that are destinations rather than entry points (the plans page and the PMA
 * payment link), so the originating touchpoint is preserved across navigation.
 */
export const mapSourceToTouchpoint = (
    source?: FrontendPaymentSource | string | null,
): FunnelTouchpoint | undefined => {
    switch (source) {
        case FrontendPaymentSource.JOB_LIST:
        case FrontendPaymentSource.JOB_LIST_SUBSCRIBED:
            return 'post_job';
        case FrontendPaymentSource.PROMOTE_JOB:
            return 'job_promotion';
        case FrontendPaymentSource.APPLICANT_DETAILS:
        case FrontendPaymentSource.APPLICANT_DETAILS_SUBSCRIBED:
        case FrontendPaymentSource.CHAT:
            return 'applicant_contacts';
        case FrontendPaymentSource.CV_SEARCH:
        case FrontendPaymentSource.APPLICANTS_LIST:
        case FrontendPaymentSource.RECOMMENDATIONS_LIST:
        case FrontendPaymentSource.PDF_CV_APPLICANT:
        case FrontendPaymentSource.PDF_CV_CV_SEARCH:
        case FrontendPaymentSource.VIDEO_CV:
            return 'profile_view';
        case FrontendPaymentSource.SAUD_VETTING:
            return 'saud_interviews';
        case FrontendPaymentSource.ADVANCED_FILTERS:
            return 'advanced_filters';
        case FrontendPaymentSource.BILLING_HISTORY_UPGRADE:
            return 'billing_page';
        default:
            return undefined;
    }
};

const readStoredTouchpoint = (): FunnelTouchpoint | undefined => {
    try {
        return (sessionStorage.getItem(TOUCHPOINT_STORAGE_KEY) as FunnelTouchpoint) || undefined;
    } catch {
        return undefined;
    }
};

const storeTouchpoint = (touchpoint: FunnelTouchpoint) => {
    try {
        sessionStorage.setItem(TOUCHPOINT_STORAGE_KEY, touchpoint);
    } catch {
        // Storage can be unavailable in private browsing / webviews; tracking must not break.
    }
};

/**
 * Resolves the touchpoint for the current funnel step, remembering the last real entry
 * point so events fired after navigating to the plans page keep the original attribution.
 */
export const resolveFunnelTouchpoint = (
    source?: FrontendPaymentSource | string | null,
): FunnelTouchpoint => {
    const mapped = mapSourceToTouchpoint(source);
    if (mapped) {
        storeTouchpoint(mapped);
        return mapped;
    }
    return readStoredTouchpoint() || DEFAULT_TOUCHPOINT;
};

export const getFunnelSurface = (
    source?: FrontendPaymentSource | string | null,
): FunnelSurface =>
    isPmaWebview() || source === FrontendPaymentSource.PMA ? 'pma' : 'pwa';

export const getFunnelUserState = (partner?: Partner | null): FunnelUserState =>
    isPaidUser(partner) ? 'paid' : 'free';

/**
 * Plan naming for the funnel events. Unlike `getPlanNameFromCategory`, this keeps
 * premium and premium_plus distinct as the funnel spec requires.
 */
export const getFunnelPlanName = (
    category?: SubscriptionPlanCategoryEnum | string | null,
): FunnelPlan | undefined => {
    switch (category) {
        case SubscriptionPlanCategoryEnum.CORE:
            return 'basic';
        case SubscriptionPlanCategoryEnum.RISE:
            return 'premium';
        case SubscriptionPlanCategoryEnum.GROWTH:
            return 'premium_plus';
        case SubscriptionPlanCategoryEnum.ENTERPRISE:
            return 'enterprise';
        case SubscriptionPlanCategoryEnum.FREE:
            return 'free';
        default:
            return undefined;
    }
};

export const mapPaymentMethodToPaymentType = (method?: PaymentMethodEnum | string) => {
    switch (method) {
        case PaymentMethodEnum.APPLE_PAY:
            return 'apple_pay';
        case PaymentMethodEnum.BANK_TRANSFER:
            return 'bank_transfer';
        case PaymentMethodEnum.CARD:
            return 'card';
        default:
            return undefined;
    }
};

/**
 * The parameters every paywall / plans-page funnel event carries. `touchpoint`,
 * `user_state` and `surface` are always present; `plan` and `qty` are added only for
 * the steps where a plan or quantity is known.
 */
export const buildFunnelParams = ({
    source,
    partner,
    planCategory,
    qty,
    extra,
}: {
    source?: FrontendPaymentSource | string | null;
    partner?: Partner | null;
    planCategory?: SubscriptionPlanCategoryEnum | string | null;
    qty?: number;
    extra?: Record<string, unknown>;
}): Record<string, unknown> => {
    const plan = getFunnelPlanName(planCategory);

    return {
        touchpoint: resolveFunnelTouchpoint(source),
        user_state: getFunnelUserState(partner),
        surface: getFunnelSurface(source),
        ...(plan ? { plan } : {}),
        ...(qty != null ? { qty } : {}),
        ...extra,
    };
};
